// components/OtpModal.js
import { Box, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useRef, useState } from "react";
import CustomSnackbar from "../../../newTestPreo/components/snackbar/Snackbar";

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;

// ================= OTP INPUT ==================
const OtpInput = ({ length = 6, onChangeOtp }) => {
  const [otp, setOtp] = useState(Array(length).fill(""));
  const inputRefs = useRef([]);

  const handleChange = (e, index) => {
    const value = e.target.value;
    if (!/^[0-9]?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < length - 1) {
      inputRefs.current[index + 1].focus();
    }

    onChangeOtp && onChangeOtp(newOtp.join(""));
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  return (
    <div
      style={{
        display: "flex",
        gap: "10px",
        justifyContent: "center",
        marginBottom: "10px",
      }}
    >
      {otp.map((value, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          maxLength="1"
          value={value}
          onChange={(e) => handleChange(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          style={{
            width: "52px",
            height: "52px",
            borderRadius: "10px",
            border: "2px solid #6B1D99",
            backgroundColor: "#F7EEFF",
            textAlign: "center",
            fontSize: "20px",
            fontFamily: "Inter, sans-serif",
            fontWeight: 600,
            outline: "none",
          }}
        />
      ))}
    </div>
  );
};

// ================= MODAL ==================
function OtpModal({
  open,
  onClose,
  onVerify,
  countryCode,
  mobile,
  email,
  loginMode,
}) {
  const [timeLeft, setTimeLeft] = useState(300);
  const [otp, setOtp] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    if (!open) return;

    setTimeLeft(300);
    setOtp("");

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [open]);

  const handleResend = async () => {
    const payload = {
      for_login: 1,
      email: null,
      mobile: null,
      mobile_country_code: null,
    };

    if (loginMode === "email") {
      payload.email = email;
    } else {
      payload.mobile = mobile;
      payload.mobile_country_code = countryCode;
    }

    try {
      const response = await fetch(
        `${API_ENDPOINT}studentsignup/register/generatingotp?platform=1`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      const result = await response.json();

      if (response.ok) {
        showSnackbar("OTP sent successfully!");
        setTimeLeft(300);
      } else {
        showSnackbar(result.message || "Failed to resend OTP", "error");
      }
    } catch (error) {
      showSnackbar("Error sending OTP", "error");
    }
  };

  const formatTime = (seconds) => {
    const min = String(Math.floor(seconds / 60)).padStart(2, "0");
    const sec = String(seconds % 60).padStart(2, "0");
    return `${min}:${sec}`;
  };

  if (!open) return null;

  const handleVerify = () => {
    if (otp.length === 6) {
      onVerify(otp);
    }
  };

  return (
    <div style={overlayStyle}>
      <div
        style={modalStyle}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleVerify();
          }
        }}
      >
        {/* Close Button */}
        <Box
          sx={{ width: "100%", display: "flex", justifyContent: "flex-end" }}
        >
          <IconButton onClick={onClose} color="inherit">
            <CloseIcon fontSize="medium" />
          </IconButton>
        </Box>

        {/* Heading */}
        <div
          style={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 600, // Semi Bold
            fontSize: "28px",
            lineHeight: "100%",
            letterSpacing: "0px",
            textAlign: "center",
            color: "#2D0A42",
            marginBottom: "20px",
          }}
        >
          OTP Verification
        </div>

        {/* Subtext */}
        <p
          style={{
            textAlign: "center",
            fontFamily: "Inter, sans-serif",
            fontSize: "16px",
            color: "#222222",
            marginBottom: "10px",
          }}
        >
          We've sent a verification code to
        </p>

        {/* Number */}
        <div
          style={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 600,
            fontSize: "16px",
            textAlign: "center",
            color: "#3D025B",
            marginBottom: "30px",
            display: "flex",
            justifyContent: "center",
            gap: "5px",
            flexWrap: "wrap",
          }}
        >
          {/* {loginMode === "email" ? email : `+${countryCode} ${mobile}`} */}
          <div>{email} <span style={{color: "#222", fontWeight: 500}}> and </span>  </div>
          <div> +{countryCode} {mobile}</div>
        </div>

        {/* OTP Inputs */}
        <OtpInput onChangeOtp={setOtp} />

        {/* Timer */}
        <p
          style={{
            textAlign: "center",
            fontFamily: "Inter, sans-serif",
            fontSize: "14px",
            color: "#000000",
            marginTop: "30px",
          }}
        >
          Code Expires in{" "}
          <span style={{ color: "#D20711", fontWeight: 600 }}>
            {formatTime(timeLeft)}
          </span>
        </p>

        {/* Verify Button */}
        <div style={{ textAlign: "center", marginTop: "25px" }}>
          <button
            onClick={handleVerify}
            disabled={otp.length !== 6}
            style={{
              width: "100%",
              padding: "14px",
              backgroundColor: otp.length === 6 ? "#3D025B" : "#D6C2E8",
              color: "white",
              border: "none",
              borderRadius: "10px",
              fontFamily: "Inter, sans-serif",
              fontSize: "16px",
              fontWeight: 600,
              cursor: otp.length === 6 ? "pointer" : "not-allowed",
              transition: "0.2s",
            }}
          >
            Verify & Continue
          </button>
        </div>

        {/* Resend Text */}
        <p
          style={{
            marginTop: "20px",
            textAlign: "center",
            fontFamily: "Inter, sans-serif",
            fontSize: "14px",
            color: "#333",
          }}
        >
          Didn’t Receive Code?{" "}
          <span
            onClick={handleResend}
            style={{
              color: "#3D025B",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Resend Code
          </span>
        </p>
      </div>

      <CustomSnackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </div>
  );
}

export default OtpModal;

// ================= STYLES ==================
const overlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1000,
};

const modalStyle = {
  background: "#fff",
  padding: "30px",
  borderRadius: "24px",
  width: "500px",
  height: "auto",
  boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
};
